import { ToolSidebar } from '@/components/workflow/ToolSidebar';
import { PropertiesPanel } from '@/components/workflow/PropertiesPanel';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { useSelectionMode } from '@/contexts/SelectionModeContext';
import type { MinimapData } from '@/components/editor/WorkflowCanvas';

export interface IndexSidebarsDeps {
  // ToolSidebar — wrapped handlers + cross-sheet flag (not in selection ctx)
  crossSheetMode: boolean;
  onToggleConnectMode: () => void;
  onToggleGroupMode: () => void;
  onToggleCrossSheetMode: () => void;
  onSelectBlock: (id: string) => void;
  onUpdateBlockColor: (id: string, color: string) => void;
  onUpdateGroupColor: (id: string, color: string) => void;
  onToggleGroupLock: (groupId: string) => void;
  onToggleGroupCollapse: (groupId: string) => void;
  onCollapseAll: () => void;
  onCollapseToLayers: () => void;
  onExpandAll: () => void;
  onNavigateToLayer: (groupId: string) => void;

  // PropertiesPanel — UI-local selection + wrapped handlers
  selectedGroup: import('@/types/workflow').BlockGroup | null;
  onDeleteBlockRequested: () => void;
  onDeleteGroupRequested: () => void;
  minimapData: MinimapData | null;
  onNavigateToGroup: (groupId: string) => void;
  onStartCrossSheetLink: (blockId: string) => void;
  /** Current workflow id — threaded into PropertiesPanel so the
   *  embedded Minimap can subscribe to LiveFilterContext. */
  currentDocumentId: string | null;
}

/**
 * Bygger sidebarLeft + sidebarRight JSX för Index.tsx. Returneras som
 * ReactNode-par så caller bara passar dem vidare till CanvasArea/IndexDesktopShell.
 * Flyttar konstruktionen ur Index.tsx (~50 rader inline-JSX).
 */
export function useIndexSidebars(deps: IndexSidebarsDeps): {
  sidebarLeft: React.ReactNode;
  sidebarRight: React.ReactNode;
} {
  const wf = useWorkflow();
  const { connectMode, groupMode, groupSelection, selectedConnectorId } = useSelectionMode();

  const sidebarLeft = (
    <ToolSidebar
      connectMode={connectMode}
      groupMode={groupMode}
      crossSheetMode={deps.crossSheetMode}
      multiSheet={wf.sheets.length > 1}
      groupingCount={groupSelection.length}
      blocks={wf.blocks}
      groups={wf.groups}
      selectedBlockId={wf.selectedBlockId}
      currentLayerGroupId={wf.currentLayerGroupId}
      onToggleConnectMode={deps.onToggleConnectMode}
      onToggleGroupMode={deps.onToggleGroupMode}
      onToggleCrossSheetMode={deps.onToggleCrossSheetMode}
      onSelectBlock={deps.onSelectBlock}
      onUpdateBlockColor={deps.onUpdateBlockColor}
      onUpdateGroupColor={deps.onUpdateGroupColor}
      onToggleGroupLock={deps.onToggleGroupLock}
      onToggleGroupCollapse={deps.onToggleGroupCollapse}
      onCollapseAll={deps.onCollapseAll}
      onCollapseToLayers={deps.onCollapseToLayers}
      onExpandAll={deps.onExpandAll}
      onNavigateToLayer={deps.onNavigateToLayer}
    />
  );

  const sidebarRight = (
    <PropertiesPanel
      selectedBlock={wf.selectedBlock}
      selectedGroup={deps.selectedGroup}
      selectedConnectorId={selectedConnectorId}
      workflowName={wf.workflowName}
      onUpdateWorkflowName={wf.setWorkflowName}
      onUpdateBlock={wf.updateBlock}
      onDeleteBlock={deps.onDeleteBlockRequested}
      onUpdateGroup={wf.updateGroup}
      onDeleteGroup={deps.onDeleteGroupRequested}
      onUpdateConnector={wf.updateConnector}
      onDeleteConnector={wf.deleteConnector}
      onUpdateConnectorWaypoints={wf.updateConnectorWaypoints}
      onCreateConnector={wf.createConnector}
      blocks={wf.blocks}
      connectors={wf.connectors}
      groups={wf.groups}
      canvasWidth={deps.minimapData?.canvasWidth ?? 4000}
      canvasHeight={deps.minimapData?.canvasHeight ?? 2500}
      viewportWidth={deps.minimapData?.viewportWidth ?? 0}
      viewportHeight={deps.minimapData?.viewportHeight ?? 0}
      scrollLeft={deps.minimapData?.scrollLeft ?? 0}
      blockDimensions={deps.minimapData?.blockDimensions ?? {}}
      scrollTop={deps.minimapData?.scrollTop ?? 0}
      zoom={deps.minimapData?.zoom ?? 1}
      onMinimapNavigate={deps.minimapData?.onNavigate ?? (() => {})}
      onNavigateToGroup={deps.onNavigateToGroup}
      sheets={wf.sheets}
      activeSheetIndex={wf.activeSheetIndex}
      crossSheetConnectors={wf.crossSheetConnectors}
      onStartCrossSheetLink={deps.onStartCrossSheetLink}
      onDeleteCrossSheetLink={wf.deleteCrossSheetConnector}
      onNavigateToSheet={wf.setActiveSheetIndex}
      workflowId={deps.currentDocumentId}
    />
  );

  return { sidebarLeft, sidebarRight };
}
